import { useMutation } from '@apollo/client';
import ConfirmationDialog from '@components/Dialog/Confirm';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { DELETE_QUESTIONNAIRE, DUPLICATE_QUESTIONNAIRE } from '@gql/questionnaire';
import { usePageSize } from '@hooks/usePageSize';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DataTableColumn } from '@components/DataTable/types';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '@components/DataTable/DataTable';
import {
    Questionnaire,
    QuestionnaireStatus,
    QUESTIONNAIRE_STATUS_LABELS,
} from './types';
import { useGetQuestionnaires } from './hooks/useGetQuestionnaires';

const ContainerWrapper = styled(Box)(({ theme }) => ({
    borderRadius: '24px',
    background: 'none',
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    gap: theme.spacing(3),
    flex: 1,
}));

const statusColor: Record<QuestionnaireStatus, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
    [QuestionnaireStatus.DRAFT]: 'warning',
    [QuestionnaireStatus.ACTIVE]: 'success',
    [QuestionnaireStatus.ARCHIVED]: 'default',
};

interface ConfirmState {
    open: boolean;
    title: string;
    description: string;
    action: string;
    cancelTitle: string;
    confirmTitle: string;
}

const CLOSED_CONFIRM: ConfirmState = {
    open: false,
    title: '',
    description: '',
    action: '',
    cancelTitle: '',
    confirmTitle: '',
};

interface QuestionnaireManagementProps {
    /** When provided, the list is scoped to this Pro (company sub-page). */
    proId?: string | null;
    /** Base path used for navigation (default: '/questionnaires'). */
    basePath?: string;
}

const QuestionnaireManagement = ({ proId, basePath = '/questionnaires' }: QuestionnaireManagementProps = {}) => {
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<QuestionnaireStatus | ''>('');
    const [page, setPage] = useState(1);
    const { pageSize, storePageSize } = usePageSize({ storageKey: 'questionnaires-page-size' });
    const [limit, setLimit] = useState(pageSize);

    const [confirm, setConfirm] = useState<ConfirmState>(CLOSED_CONFIRM);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [selectedId, setSelectedId] = useState<string>('');
    const [confirmAction, setConfirmAction] = useState<'delete' | 'duplicate'>('delete');

    // Debounce the search query
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQuery(query), 350);
        return () => clearTimeout(t);
    }, [query]);

    const { questionnaires, totalDocs, loading, refetch } = useGetQuestionnaires({
        query: debouncedQuery,
        status: statusFilter || null,
        proId: proId || null,
        page,
        limit,
    });

    const [deleteQuestionnaire] = useMutation(DELETE_QUESTIONNAIRE);
    const [duplicateQuestionnaire] = useMutation(DUPLICATE_QUESTIONNAIRE);

    const handleDelete = async (id: string) => {
        try {
            await deleteQuestionnaire({ variables: { id } });
            showSnackbar('Questionnaire supprimé avec succès', 'success');
            await refetch();
        } catch (error: any) {
            showSnackbar(error?.message || 'Erreur lors de la suppression', 'error');
        }
    };

    const handleDuplicate = async (id: string) => {
        try {
            await duplicateQuestionnaire({ variables: { id } });
            showSnackbar('Questionnaire dupliqué avec succès', 'success');
            await refetch();
        } catch (error: any) {
            showSnackbar(error?.message || 'Erreur lors de la duplication', 'error');
        }
    };

    const handleConfirmAction = async () => {
        setConfirmLoading(true);
        try {
            if (confirmAction === 'delete') {
                await handleDelete(selectedId);
            } else {
                await handleDuplicate(selectedId);
            }
        } finally {
            setConfirmLoading(false);
            setConfirm(CLOSED_CONFIRM);
        }
    };

    const openConfirm = (row: Questionnaire, action: 'delete' | 'duplicate') => {
        setSelectedId(row._id);
        setConfirmAction(action);
        setConfirm(
            action === 'delete'
                ? {
                      open: true,
                      title: 'Supprimer le questionnaire',
                      description: `Êtes-vous sûr de vouloir supprimer "${row.title}" ? Cette action est irréversible.`,
                      action: 'delete',
                      cancelTitle: 'Annuler',
                      confirmTitle: 'Supprimer',
                  }
                : {
                      open: true,
                      title: 'Dupliquer le questionnaire',
                      description: `Créer une copie de "${row.title}" en tant que brouillon ?`,
                      action: 'duplicate',
                      cancelTitle: 'Annuler',
                      confirmTitle: 'Dupliquer',
                  },
        );
    };

    const columns: DataTableColumn<Questionnaire>[] = useMemo(
        () => [
            {
                id: 'title',
                label: 'Titre',
                field: 'title',
                minWidth: 220,
                renderCell: ({ row }) => (
                    <Typography variant="body2" fontWeight={600}>
                        {row.title}
                    </Typography>
                ),
            },
            {
                id: 'difficultyLevel',
                label: 'Difficulté',
                field: 'difficultyLevel',
                width: 110,
                align: 'center',
                renderCell: ({ row }) => (
                    <Chip size="small" label={`${row.difficultyLevel}/10`} variant="outlined" />
                ),
            },
            {
                id: 'questionsCount',
                label: 'Questions',
                width: 110,
                align: 'center',
                renderCell: ({ row }) => (
                    <Typography variant="body2">{row.questions?.length ?? 0}</Typography>
                ),
            },
            {
                id: 'duration',
                label: 'Durée',
                field: 'duration',
                width: 110,
                align: 'center',
                renderCell: ({ row }) => (
                    <Typography variant="body2">
                        {row.duration ? `${row.duration} min` : '-'}
                    </Typography>
                ),
            },
            {
                id: 'proId',
                label: 'Établissement',
                field: 'proId',
                width: 140,
                renderCell: ({ row }) => (
                    <Typography variant="body2">
                        {row.pro ? row.pro.commercialName : row.proId ?? '-'}
                    </Typography>
                ),
            },
            {
                id: 'jobOffersCount',
                label: 'Offres liées',
                width: 120,
                align: 'center',
                renderCell: ({ row }) => (
                    <Typography variant="body2">{row.jobOffers?.length ?? 0}</Typography>
                ),
            },
            {
                id: 'status',
                label: 'Statut',
                field: 'status',
                width: 130,
                renderCell: ({ row }) => (
                    <Chip
                        size="small"
                        color={statusColor[row.status as QuestionnaireStatus]}
                        label={QUESTIONNAIRE_STATUS_LABELS[row.status as QuestionnaireStatus] ?? row.status}
                    />
                ),
            },
            {
                id: 'actions',
                label: 'Actions',
                width: 160,
                align: 'right',
                renderCell: ({ row }) => (
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => navigate(`${basePath}/${row._id}/edit`)}>
                            Modifier
                        </Button>
                        <Button size="small" onClick={() => openConfirm(row, 'duplicate')}>
                            Dupliquer
                        </Button>
                        <Button size="small" color="error" onClick={() => openConfirm(row, 'delete')}>
                            Supprimer
                        </Button>
                    </Stack>
                ),
            },
        ],
        [navigate, basePath],
    );

    return (
        <ContainerWrapper>
            <ConfirmationDialog
                details={confirm}
                setOpen={() => setConfirm(CLOSED_CONFIRM)}
                handleModalAction={handleConfirmAction}
                isLoadingSubmit={confirmLoading}
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flex={1}>
                    <TextField
                        size="small"
                        placeholder="Rechercher un questionnaire..."
                        value={query}
                        onChange={e => {
                            setQuery(e.target.value);
                            setPage(1);
                        }}
                        sx={{ minWidth: 280 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">🔍</InputAdornment>,
                            endAdornment: loading ? (
                                <InputAdornment position="end">
                                    <CircularProgress size={16} />
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <TextField
                        size="small"
                        select
                        value={statusFilter}
                        onChange={e => {
                            setStatusFilter(e.target.value as QuestionnaireStatus | '');
                            setPage(1);
                        }}
                        sx={{ minWidth: 180 }}
                        label="Statut">
                        <MenuItem value="">Tous les statuts</MenuItem>
                        <MenuItem value={QuestionnaireStatus.DRAFT}>Brouillon</MenuItem>
                        <MenuItem value={QuestionnaireStatus.ACTIVE}>Actif</MenuItem>
                        <MenuItem value={QuestionnaireStatus.ARCHIVED}>Archivé</MenuItem>
                    </TextField>
                </Stack>
                <Button variant="contained" onClick={() => navigate(`${basePath}/add`)}>
                    + Créer un questionnaire
                </Button>
            </Stack>

            <DataTable
                rows={questionnaires}
                columns={columns}
                loading={loading}
                pagination={{
                    currentPage: page,
                    pageSize: limit,
                    totalRows: totalDocs,
                    onPageChange: p => setPage(p),
                    onPageSizeChange: ps => {
                        setLimit(ps);
                        storePageSize(ps);
                        setPage(1);
                    },
                }}
                onRowClick={row => navigate(`${basePath}/${row._id}/edit`)}
            />
        </ContainerWrapper>
    );
};

export default QuestionnaireManagement;
